import { useState, useEffect, useRef } from 'react';
import { EType } from '@/types/goal';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import type { MouseEvent } from 'react';
import { Plus, BookOpen, Eye, Zap, CalendarIcon, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDateFormat } from '@/contexts/DateFormatContext';
import { useAuth } from '@/hooks/useAuth';
import { canCreateGoal, getUserTier, TIER_LIMITS } from '@/lib/tier-utils';
import { UpgradeModal } from './subscription/UpgradeModal';
import { trackLimitHit } from '@/lib/analytics';
import { BETA_CONFIG } from '@/config/beta';

interface CreateGoalDialogProps {
  onCreateGoal: (title: string, description: string, eTypes: EType[], targetDate?: Date) => void | Promise<any>;
  triggerVariant?: 'default' | 'link';
}

const eTypeOptions: { type: EType; label: string; icon: React.ReactNode; description: string }[] = [
  {
    type: 'education',
    label: 'Education',
    icon: <BookOpen className="w-5 h-5" />,
    description: 'Learning, courses, reading',
  },
  {
    type: 'exposure',
    label: 'Exposure',
    icon: <Eye className="w-5 h-5" />,
    description: 'Networking, observing, mentorship',
  },
  {
    type: 'experience',
    label: 'Experience',
    icon: <Zap className="w-5 h-5" />,
    description: 'Doing, practicing, building',
  },
];

const MAX_TITLE_LENGTH = 200;
const GOAL_TITLE_PATTERN = /^[a-zA-Z0-9\s\-',.!?()]+$/;
const GOAL_TITLE_ERROR = 'Goal titles can only contain letters, numbers, and basic punctuation';

export const CreateGoalDialog = ({ onCreateGoal, triggerVariant = 'default' }: CreateGoalDialogProps) => {
  const { user } = useAuth();
  const { formatDate } = useDateFormat();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<EType[]>([]);
  const [targetDate, setTargetDate] = useState<Date | undefined>(undefined);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [titleError, setTitleError] = useState('');
  const submittingRef = useRef(false);

  // Tier-based state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isAtLimit, setIsAtLimit] = useState(false);
  const [currentCount, setCurrentCount] = useState(0);
  const [limit, setLimit] = useState(TIER_LIMITS.free.max_goals);

  useEffect(() => {
    if (user?.id) {
      checkLimits();
    }
  }, [user?.id]);

  const checkLimits = async () => {
    if (!user?.id) return;
    
    const result = await canCreateGoal(user.id);
    setIsAtLimit(!BETA_CONFIG.allFeaturesUnlocked && !result.allowed);
    setCurrentCount(result.currentCount || 0);
    setLimit(result.limit || TIER_LIMITS.free.max_goals);
  };

  const handleTriggerClick = async (e: MouseEvent) => {
    if (!user?.id) {
      e.preventDefault();
      return;
    }

    // In beta mode all features are unlocked — let DialogTrigger open natively
    if (BETA_CONFIG.allFeaturesUnlocked) return;

    // Non-beta: prevent native open, do async tier check first
    e.preventDefault();

    try {
      const result = await canCreateGoal(user.id);

      if (!result.allowed) {
        trackLimitHit('goal', { currentCount: result.currentCount, limit: result.limit });
        setShowUpgradeModal(true);
        return;
      }
    } catch {
      // If limit check fails, allow goal creation as a safe fallback
    }

    setOpen(true);
  };

  const toggleType = (type: EType) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const validateTitle = (value: string): boolean => {
    if (!value.trim()) {
      setTitleError('');
      return false;
    }
    if (!GOAL_TITLE_PATTERN.test(value.trim())) {
      setTitleError(GOAL_TITLE_ERROR);
      return false;
    }
    setTitleError('');
    return true;
  };

  const handleSubmit = async () => {
    if (submittingRef.current) return;
    if (!title.trim() || selectedTypes.length === 0 || isSubmitting) return;
    if (!validateTitle(title)) return;

    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      await onCreateGoal(title.trim(), description.trim(), selectedTypes, targetDate);
      setTitle('');
      setDescription('');
      setSelectedTypes([]);
      setTargetDate(undefined);
      setOpen(false);
      // Refresh limits after creating
      checkLimits();
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          {triggerVariant === 'link' ? (
            <button
              className="text-sm text-primary hover:text-primary/80 underline underline-offset-2 transition-colors"
              data-testid="create-goal-trigger"
              onClick={handleTriggerClick}
            >
              create your own
            </button>
          ) : (
            <Button
              variant="gradient"
              size="lg"
              className="gap-2"
              data-testid="create-goal-trigger"
              onClick={handleTriggerClick}
            >
              {isAtLimit ? (
                <>
                  <Lock className="w-4 h-4" />
                  New Goal
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5" />
                  Create Goal
                </>
              )}
            </Button>
          )}
        </DialogTrigger>
        <DialogContent className="sm:max-w-lg max-h-[85dvh]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-display">Create New Goal</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 pt-4 pb-2">
            <div className="space-y-2">
              <Label htmlFor="goal-title">Goal Title</Label>
              <Input
                id="goal-title"
                value={title}
                onChange={(e) => {
                  const val = e.target.value.slice(0, MAX_TITLE_LENGTH);
                  setTitle(val);
                  if (titleError) validateTitle(val);
                }}
                onBlur={() => { if (title.trim()) validateTitle(title); }}
                placeholder="e.g., Master public speaking"
                maxLength={MAX_TITLE_LENGTH}
                aria-describedby="goal-title-count goal-title-error"
                className={titleError ? 'border-destructive' : ''}
              />
              {titleError && (
                <p id="goal-title-error" className="text-xs text-destructive" role="alert">
                  {titleError}
                </p>
              )}
              <p
                id="goal-title-count"
                className={cn(
                  "text-xs text-right",
                  title.length > MAX_TITLE_LENGTH * 0.9
                    ? "text-orange-500"
                    : "text-muted-foreground"
                )}
              >
                {title.length}/{MAX_TITLE_LENGTH}
              </p>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What does achieving this goal look like?"
                rows={3}
                className="resize-none"
              />
            </div>

            <div className="space-y-2">
              <Label>Target Completion Date (optional)</Label>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen} modal>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !targetDate && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {targetDate ? formatDate(targetDate, 'long') : 'Pick a target date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    captionLayout="dropdown-buttons"
                    fromYear={new Date().getFullYear()}
                    toYear={new Date().getFullYear() + 3}
                    selected={targetDate}
                    onSelect={(date) => {
                      setTargetDate(date);
                      if (date) setCalendarOpen(false);
                    }}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              {targetDate && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setTargetDate(undefined)}
                  className="text-muted-foreground"
                >
                  Clear date
                </Button>
              )}
            </div>

            <div className="space-y-3">
              <Label>
                Development Types <span className="text-muted-foreground">(select at least one)</span>
              </Label>
              <div className="grid grid-cols-1 gap-3">
                {eTypeOptions.map(({ type, label, icon, description }) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => toggleType(type)}
                    className={cn(
                      'flex items-center gap-4 p-4 rounded-lg border transition-all text-left',
                      selectedTypes.includes(type)
                        ? type === 'education'
                          ? 'border-education bg-education/10'
                          : type === 'exposure'
                          ? 'border-exposure bg-exposure/10'
                          : 'border-experience bg-experience/10'
                        : 'border-border hover:border-primary/50 hover:bg-secondary/50'
                    )}
                  >
                    <div
                      className={cn(
                        'p-2 rounded-lg',
                        type === 'education' && 'bg-education/20 text-education',
                        type === 'exposure' && 'bg-exposure/20 text-exposure',
                        type === 'experience' && 'bg-experience/20 text-experience'
                      )}
                    >
                      {icon}
                    </div>
                    <div>
                      <div className="font-semibold">{label}</div>
                      <div className="text-sm text-muted-foreground">{description}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <Button
              onClick={handleSubmit}
              disabled={!title.trim() || !!titleError || selectedTypes.length === 0 || isSubmitting}
              className="w-full"
              variant="gradient"
              size="lg"
            >
              {isSubmitting ? 'Creating...' : 'Create Goal'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        trigger="goal_limit"
      />
    </>
  );
};